// The two user manuals (public/manuals) and where each screen is explained.
//
// Pages are PDF page numbers, the number `#page=` opens. They are one more
// than the number printed in the manual's footer, because the cover is not
// numbered. When a manual is replaced:
//   1. overwrite the PDF and bump MANUALS_VERSION, so browsers fetch it again;
//   2. check every page below: the heading in MANUAL_ANCHORS must be on it
//      (pdftotext -f N -l N file.pdf - | grep "heading").
//
// Only projects behind the meter and merchant projects have a manual; every
// other revenue model shows no link at all.

export const MANUALS_VERSION = "2026-11-03b";

const base = () => process.env.PUBLIC_URL || "";

export const MANUAL_FILES = {
  btm: "pythia-manual-btm.pdf",
  merchant: "pythia-manual-merchant.pdf",
};

export const MANUAL_URLS = {
  btm: `${base()}/manuals/${MANUAL_FILES.btm}?v=${MANUALS_VERSION}`,
  merchant: `${base()}/manuals/${MANUAL_FILES.merchant}?v=${MANUALS_VERSION}`,
};

export const MANUAL_PAGES = {
  btm: {
    regime: 16, // 5.5 Καθεστώς έγχυσης και εξαγωγή πλεονάσματος
    battery: 33, // 7 Ρύθμιση μονάδας: η μπαταρία (BESS)
    consumption: 46, // 11.2 Ζήτηση: η κατανάλωση της εγκατάστασης
    tariffs: 49, // 11.3 Τιμολόγια: το κόστος ρεύματος του πελάτη
    economics: 52, // 12 Μελέτη 4/5 — Οικονομικές παραδοχές
    simulation: 58, // 13 Μελέτη 5/5 — Προσομοίωση
    reports: 60, // 14 Αναφορές και προσφορές προς τον πελάτη
    vnb: 77, // 19.4 Εικονικός συμψηφισμός: το καθεστώς έγχυσης
    vnbSettlement: 78, // 19.5 Εκκαθάριση: χρέωση ΛΠ1-3 και πλεόνασμα
    vnbSupplies: 79, // 19.6 Παροχές και ποσοστά επιμερισμού
    pvSizing: 83, // 20 Οικονομική διαστασιολόγηση Φ/Β
    pvSizingResults: 91, // 20.7 Ανάλυση ισχύος (Στάδιο 1)
    quickStudy: 102, // 21.1 Το παράθυρο «Γρήγορη μελέτη»
    studyResults: 107, // 21.5 Η κάρτα «Αποτελέσματα μελέτης»
    preRunChecks: 109, // 21.6 Έλεγχοι πριν από την εκτέλεση
  },
  merchant: {
    battery: 26, // 7 Η μπαταρία
    consumption: 41, // 11 Τεχνικές παραδοχές
    tariffs: 41, // 11 Τεχνικές παραδοχές
    economics: 42, // 12 Οικονομικές παραδοχές
    simulation: 49, // 13 Εκτέλεση της μελέτης
    reports: 52, // 14 Οι αναφορές
  },
};

/** The heading each page must carry; checked when a manual is replaced. */
export const MANUAL_ANCHORS = {
  btm: {
    regime: "5.5 Καθεστώς έγχυσης",
    battery: "η μπαταρία (BESS)",
    consumption: "11.2 Ζήτηση",
    tariffs: "11.3 Τιμολόγια",
    economics: "Οικονομικές παραδοχές",
    simulation: "Προσομοίωση",
    reports: "Αναφορές και προσφορές",
    vnb: "19.4 Ρύθμιση μονάδας: το καθεστώς έγχυσης",
    vnbSettlement: "19.5 Εκκαθάριση",
    vnbSupplies: "19.6 Παροχές και ποσοστά επιμερισμού",
    pvSizing: "Οικονομική διαστασιολόγηση Φ/Β",
    pvSizingResults: "20.7 Ανάλυση ισχύος",
    quickStudy: "21.1 Το παράθυρο «Γρήγορη μελέτη»",
    studyResults: "21.5 Η κάρτα «Αποτελέσματα μελέτης»",
    preRunChecks: "21.6 Έλεγχοι πριν από την εκτέλεση",
  },
  merchant: {
    battery: "Η μπαταρία",
    consumption: "Τεχνικές παραδοχές",
    tariffs: "Τεχνικές παραδοχές",
    economics: "Οικονομικές παραδοχές",
    simulation: "Εκτέλεση της μελέτης",
    reports: "Οι αναφορές",
  },
};

/** "btm", "merchant" or null (no manual for this revenue model). */
export function manualFor(settlementType) {
  const type = String(settlementType || "").trim();
  if (type.toUpperCase() === "BTM") return "btm";
  if (type === "Arbitrage") return "merchant";
  return null;
}

/** { manual, page, url } for a screen, or null when there is nothing to show. */
export function manualLink(settlementType, section) {
  const manual = manualFor(settlementType);
  const page = manual ? MANUAL_PAGES[manual][section] : null;
  if (!manual || !page) return null;
  return { manual, page, url: `${MANUAL_URLS[manual]}#page=${page}` };
}
